import React, { useState, useEffect } from "react";
import { ArrowRight, ExternalLink, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link, useNavigate } from "react-router-dom";
import PublicHeader from "@/components/public/PublicHeader";
import PublicFooter from "@/components/public/PublicFooter";
import ProductShots from "@/components/home/ProductShots";
import RegulationMap from "@/components/home/RegulationMap";
import FoundingUsers from "@/components/home/FoundingUsers";
import FounderNote from "@/components/home/FounderNote";
import AppHero from "@/components/home/AppHero";
import ConnectedPlatform from "@/components/home/ConnectedPlatform";
import PlatformFeatures from "@/components/home/PlatformFeatures";
import MultiLocationSection from "@/components/home/MultiLocationSection";
import { SUPPORTED_REGIONS } from "@/lib/compliance-copy";
import HomeFaq from "@/components/home/HomeFaq";
import { BLOG_POSTS } from "@/data/blog";

const howItWorks = [
  { step: "1", title: "Sign up", desc: "Create your org, invite your team, pick your region & HACCP presets." },
  { step: "2", title: "Run checklists", desc: "Use the liquid‑glass UI to blaze through tasks without friction." },
  { step: "3", title: "Export & share", desc: "One‑tap reports for audits and daily ops. Easy mode." },
];

const regionalSteps = [
  { title: "Select a region", desc: "Each business or location selects the region it operates in." },
  { title: "Apply the law pack", desc: "The supported regional law pack, guidance and procedures are loaded for that location." },
  { title: "Review changes", desc: "When a supported requirement changes, the update is presented for review before it is applied." },
  { title: "Keep the record", desc: "Applied updates are recorded so you can show what changed and when." },
];

export default function Index() {
  const [showCookie, setShowCookie] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const consent = localStorage.getItem("crystalhaccp_cookie_consent");
    if (!consent) setShowCookie(true);
  }, []);

  function acceptCookies() {
    localStorage.setItem("crystalhaccp_cookie_consent", "true");
    setShowCookie(false);
  }

  return (
    <div className="min-h-screen bg-crystal-charcoal">
      <div className="fixed inset-0 bg-[radial-gradient(1200px_600px_at_80%_-20%,rgba(155,135,245,0.15),transparent),radial-gradient(900px_500px_at_-10%_10%,rgba(74,222,128,0.08),transparent),radial-gradient(800px_400px_at_50%_50%,rgba(249,115,22,0.05),transparent)] pointer-events-none" />

      <PublicHeader />

      <main>
        <AppHero />
        <ConnectedPlatform />

        {/* WHAT IS CRYSTAL HACCP */}
        <section className="relative bg-crystal-charcoal/50 py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="heading-app text-3xl md:text-4xl mb-6">What is Crystal HACCP?</h2>
            <p className="text-white/80 text-lg leading-relaxed mb-4">
              Crystal HACCP is a food safety management platform for restaurants, cafes, catering
              businesses and food manufacturers. Crystalhaccp.com is the web dashboard where the
              HACCP system, locations, staff, documents and reports are managed. The mobile app is
              where the team completes daily checks, temperature records, inspections and corrective
              actions.
            </p>
            <p className="text-white/70 text-base leading-relaxed">
              Activity completed in the mobile app is synchronised with the web dashboard, so
              management sees inspections, exceptions and open actions across every location.
            </p>
          </div>
        </section>

        <PlatformFeatures />

        {/* REGIONAL SUPPORT */}
        <section id="regions" className="relative">
          <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
            <h2 className="heading-app text-3xl md:text-4xl mb-4">Supports regional compliance</h2>
            <p className="text-white/75 max-w-3xl mb-8">
              Each business or location selects its region. That selection determines which supported
              law pack, guidance and procedures are shown. Important changes are presented for review
              before they are applied.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {SUPPORTED_REGIONS.map((region) => (
                <div key={region.code} className="app-card p-5">
                  <div className="text-white font-semibold">{region.name}</div>
                  <p className="text-white/65 text-sm mt-1.5">{region.note}</p>
                </div>
              ))}
            </div>

            <h3 className="heading-app text-xl md:text-2xl mt-12 mb-5">How it works</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {regionalSteps.map((s, i) => (
                <div key={s.title} className="app-card p-5">
                  <div className="app-eyebrow mb-2">Step {i + 1}</div>
                  <div className="text-white font-semibold">{s.title}</div>
                  <p className="text-white/65 text-sm mt-1.5">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ProductShots />
        <MultiLocationSection />
        <RegulationMap />
        <FoundingUsers />
        <FounderNote />
        <section className="relative border-t border-border">
          <div className="mx-auto max-w-6xl px-4 py-16">
            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
              <h2 className="heading-app text-3xl text-foreground">From the food-safety blog</h2>
              <Link to="/blog" className="inline-flex items-center gap-2 text-accent-on-dark">All articles <ArrowRight size={16} /></Link>
            </div>
            <div className="grid gap-8 md:grid-cols-3">
              {BLOG_POSTS.slice(0, 3).map((post) => (
                <article key={post.slug} className="border-t border-border pt-5">
                  <p className="mb-2 text-sm text-accent-on-dark">{post.category}</p>
                  <h3 className="text-xl text-foreground"><Link to={`/blog/${post.slug}`} className="hover:underline">{post.title}</Link></h3>
                  <p className="mt-3 text-sm text-muted-foreground">{post.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        <HomeFaq />

        {/* HOW IT WORKS */}
        <section id="how" className="relative">
          <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
            <h2 className="heading-app text-3xl md:text-4xl mb-8">From zero to compliant in minutes</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {howItWorks.map((step) => (
                <div key={step.step} className="glass-panel rounded-3xl p-6">
                  <div className="text-muted text-sm">Step {step.step}</div>
                  <div className="text-white text-xl font-semibold mt-1">{step.title}</div>
                  <p className="text-white/70 text-sm mt-2">{step.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <Button onClick={() => navigate("/auth?mode=signup")} className="cta-primary px-8 py-4 font-semibold">
                Get Started <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* CONTACT */}
        <section id="contact" className="relative">
          <div className="max-w-3xl mx-auto px-4 py-16 md:py-24 text-center">
            <h2 className="heading-app text-3xl md:text-4xl mb-4">Contact</h2>
            <p className="text-white/70 mb-8">
              One address, read by me. Questions about pricing, setup, the product or your data — all fine.
            </p>
            <Card className="glass-panel border-white/20 rounded-3xl overflow-hidden inline-block">
              <CardContent className="p-8">
                <div className="flex justify-center mb-4">
                  <div className="p-3 rounded-xl bg-crystal-orange/10 border border-crystal-orange/20">
                    <FileText className="h-6 w-6 text-crystal-orange" />
                  </div>
                </div>
                <a
                  href="mailto:info@crystalhaccp.com"
                  className="text-accent-on-dark hover:text-crystal-orange-glow font-semibold text-lg transition-colors"
                >
                  info@crystalhaccp.com
                </a>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA */}
        <section className="relative">
          <div className="max-w-6xl mx-auto px-4 pb-24">
            <div className="glass-panel rounded-[32px] p-8 md:p-12 text-center shadow-glass-elevated">
              <h3 className="text-white heading-app text-2xl md:text-3xl mb-4">
                Ready to run HACCP the easy way?
              </h3>
              <p className="text-white/70 mb-6">Open the dashboard and get moving. No tutorials. No fluff.</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button onClick={() => navigate("/dashboard")} className="glass-button px-6 py-3 font-semibold">
                  Open Dashboard <ExternalLink size={18} className="ml-2" />
                </Button>
                <Button onClick={() => navigate("/auth?mode=signup")} className="cta-primary px-6 py-3 font-semibold">
                  Start your 7-day trial <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />

      {showCookie && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 z-50">
          <div className="max-w-xl glass-panel rounded-2xl text-white p-4 shadow-glass-elevated">
            <div className="text-sm">
              We use minimal cookies for auth and analytics. By using Crystal HACCP, you agree to our{" "}
              <Link to="/cookies" className="underline">cookie policy</Link>.
            </div>
            <div className="mt-3 flex gap-2">
              <Button onClick={acceptCookies} className="cta-primary font-semibold shrink-0">
                Accept
              </Button>
              <Link to="/privacy-policy" className="px-3 py-2 text-sm underline self-center">
                Privacy policy
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
